// src/services/moderation/index.js
// Community post moderation.
//
// Three layers:
//   1. Client-side (this file) — fast UX rejection before any server call
//   2. Cloud Function (functions/moderation.js) — authoritative server check
//   3. Community signals — confirm/flag counts drive post visibility
//
// What lives here vs on the server:
//   Client:  profanity, spam patterns, duplicate detection, rate limiting, validation
//   Server:  same checks + IP rate limiting, ML abuse detection, final authority

import { withRetry } from '@services/storage/firebase';

// ── Profanity filter ──────────────────────────────────────────────
// Maintain a minimal list — enough to catch accidental and obvious violations
// without a large dependency. This is not adversarial hardening; that's the server.
// For a public emergency app, the bar is "would this embarrass a community meeting".
//
// Patterns use word boundaries so "class" doesn't match a substring.
// The list is intentionally small — add entries when specific issues arise.
const PROFANITY_PATTERNS = [
  /\bf+u+c+k+\w*/i,
  /\bs+h+i+t+\w*/i,
  /\ba+s+s+h+o+l+e+\w*/i,
  /\bb+i+t+c+h+\w*/i,
  /\bn+i+g+\w{2,}/i,      // racial slur root
  /\bf+a+g+\w*/i,          // homophobic slur
  /\bc+u+n+t+\w*/i,
];

export function containsProfanity(text) {
  if (!text) return false;
  const normalized = text.normalize('NFKD').replace(/[\u0300-\u036f]/g, ''); // strip diacritics
  return PROFANITY_PATTERNS.some((p) => p.test(normalized));
}

// SOS posts bypass profanity filter — someone in genuine distress
// should not have their help request silently rejected.
export function filterProfanity(text) {
  if (!text) return text;
  let result = text;
  for (const pattern of PROFANITY_PATTERNS) {
    result = result.replace(pattern, (match) => match[0] + '*'.repeat(match.length - 1));
  }
  return result;
}

// ── Spam detection ────────────────────────────────────────────────
const SPAM_PATTERNS = [
  { pattern: /(.)(\1){6,}/,         reason: 'Repeated characters' },
  { pattern: /https?:\/\//i,        reason: 'URLs not allowed in posts' },
  { pattern: /\b(buy|sell|discount|promo|coupon|click here|free money|cash prize)\b/i,
             reason: 'Commercial content not allowed' },
  { pattern: /\b\d{10,}\b/,         reason: 'Long number strings not allowed' },
  // All-caps posts over 30 chars are usually shouting/spam (SOS excepted — handled upstream)
  { pattern: null, fn: (t) => t.length > 30 && t === t.toUpperCase() && /[A-Z]{10,}/.test(t),
             reason: 'All-caps posts are not allowed' },
];

export function detectSpam(body) {
  if (!body || body.trim().length < 5) {
    return { spam: true, reason: 'Post is too short (minimum 5 characters)' };
  }
  if (body.trim().length > 1000) {
    return { spam: true, reason: 'Post exceeds 1000 character limit' };
  }
  for (const { pattern, fn, reason } of SPAM_PATTERNS) {
    const matches = pattern ? pattern.test(body) : fn?.(body.trim());
    if (matches) return { spam: true, reason };
  }
  return { spam: false };
}

// ── Duplicate suppression ─────────────────────────────────────────
// Two signals:
//   1. Exact duplicate: identical body from the same session (form resubmit)
//   2. Near-duplicate: same location + similar content within 30 minutes
//
// We use a simple bigram similarity (Sørensen–Dice) — fast, no deps.
// Threshold: 0.85 = "essentially the same post".

const _recentPosts = [];  // { body, lat, lon, zone, ts }
const NEAR_DUP_WINDOW_MS  = 30 * 60_000;
const NEAR_DUP_THRESHOLD  = 0.82;
const NEAR_DUP_RADIUS_DEG = 0.008;  // ~0.5 mile

export function checkDuplicate(body, lat, lon, zone) {
  const now = Date.now();
  // Purge old entries
  while (_recentPosts.length && _recentPosts[0].ts < now - NEAR_DUP_WINDOW_MS) {
    _recentPosts.shift();
  }

  const normalBody = _normalizeText(body);

  for (const prev of _recentPosts) {
    // Same zone or same location
    const sameZone = zone && prev.zone === zone;
    const nearLoc  = lat && lon && prev.lat && prev.lon
      && Math.abs(lat - prev.lat) < NEAR_DUP_RADIUS_DEG
      && Math.abs(lon - prev.lon) < NEAR_DUP_RADIUS_DEG;

    if (!sameZone && !nearLoc) continue;

    const sim = _diceSimilarity(normalBody, _normalizeText(prev.body));
    if (sim >= NEAR_DUP_THRESHOLD) {
      const agoMin = Math.round((now - prev.ts) / 60_000);
      return {
        duplicate: true,
        reason:    `Similar post already submitted ${agoMin < 1 ? 'just now' : `${agoMin}m ago`}. Add new info or confirm that post instead.`,
        similarity: sim,
      };
    }
  }

  return { duplicate: false };
}

export function recordPostForDedupe(body, lat, lon, zone) {
  _recentPosts.push({ body, lat: lat ?? null, lon: lon ?? null, zone: zone ?? null, ts: Date.now() });
}

// ── Full pre-submit check ─────────────────────────────────────────
// Runs all client-side checks in order. Returns the first failure.
// SOS posts skip profanity and all-caps checks.
export function runModerationChecks(post, { isSOS = false } = {}) {
  const { body, lat, lon, zone } = post;

  // 1. Spam
  const spam = detectSpam(body);
  if (spam.spam) return { allowed: false, reason: spam.reason, code: 'spam' };

  // 2. Profanity (not for SOS)
  if (!isSOS && containsProfanity(body)) {
    return { allowed: false, reason: 'Post contains inappropriate language. Please revise.', code: 'profanity' };
  }

  // 3. Duplicate
  const dup = checkDuplicate(body, lat, lon, zone);
  if (dup.duplicate) return { allowed: false, reason: dup.reason, code: 'duplicate' };

  return { allowed: true };
}

// ── Rate limiting ─────────────────────────────────────────────────
const _postTimestamps   = [];
const MAX_POSTS_PER_10M = 5;
const SOS_COOLDOWN_MS   = 30_000;  // 30 seconds between SOS broadcasts
let   _lastSOS          = 0;

export function checkRateLimit(isSOS = false) {
  const now = Date.now();

  if (isSOS) {
    if (now - _lastSOS < SOS_COOLDOWN_MS) {
      const wait = Math.ceil((SOS_COOLDOWN_MS - (now - _lastSOS)) / 1000);
      return { allowed: false, reason: `Please wait ${wait} second${wait !== 1 ? 's' : ''} before sending another SOS.` };
    }
    return { allowed: true };
  }

  const windowMs = 10 * 60_000;
  while (_postTimestamps.length && _postTimestamps[0] < now - windowMs) {
    _postTimestamps.shift();
  }
  if (_postTimestamps.length >= MAX_POSTS_PER_10M) {
    const wait = Math.ceil((_postTimestamps[0] + windowMs - now) / 60_000);
    return { allowed: false, reason: `Too many posts — try again in ${wait} minute${wait > 1 ? 's' : ''}.` };
  }
  return { allowed: true };
}

export function recordPostSubmission(isSOS = false) {
  if (isSOS) { _lastSOS = Date.now(); return; }
  _postTimestamps.push(Date.now());
}

// ── Confidence scoring ────────────────────────────────────────────
export function computeConfidenceScore(post) {
  const confirms = post.confirmCount ?? 0;
  const flags    = post.flagCount    ?? 0;
  const ageMs    = Date.now() - new Date(post.createdAt).getTime();
  const ageMin   = ageMs / 60_000;

  let score = 50;
  score += confirms * 8;
  score -= flags    * 15;
  score  = Math.max(0, Math.min(100, score));

  if ((post.severity ?? 1) < 4 && ageMin > 120) {
    const decay = Math.min(30, Math.floor((ageMin - 120) / 60) * 5);
    score = Math.max(0, score - decay);
  }
  return score;
}

export function isPostVisible(post) {
  if (post.isHidden)                return false;
  if ((post.flagCount ?? 0) >= 5)   return false;
  const score = computeConfidenceScore(post);
  if (score < 10 && (post.severity ?? 1) < 4) return false;
  return true;
}

export function sortPosts(posts) {
  return [...posts].sort((a, b) => {
    const sevDiff = (b.severity ?? 1) - (a.severity ?? 1);
    if (sevDiff !== 0) return sevDiff;
    const confDiff = computeConfidenceScore(b) - computeConfidenceScore(a);
    if (Math.abs(confDiff) > 10) return confDiff;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

// ── SOS validation ────────────────────────────────────────────────
// Track recent SOS submissions by subtype for device-level dedup
const _recentSOS = {};
const SOS_DEDUP_WINDOW_MS = 10 * 60_000; // 10 min per subtype

export function validateSOSPost(post) {
  if (!post.body || post.body.trim().length < 10) {
    return { valid: false, reason: 'Please describe your situation (minimum 10 characters).' };
  }
  if (!post.lat && !post.address) {
    return { valid: false, reason: 'SOS posts require a location. Use GPS or enter your address.' };
  }
  // Device-level dedup: same subtype within 10 minutes
  const subtypeKey = post.sosSubtype ?? 'other';
  const lastSent   = _recentSOS[subtypeKey] ?? 0;
  if (Date.now() - lastSent < SOS_DEDUP_WINDOW_MS) {
    const wait = Math.ceil((SOS_DEDUP_WINDOW_MS - (Date.now() - lastSent)) / 60_000);
    return { valid: false, reason: `You recently sent a ${subtypeKey.replace('_',' ')} request. Wait ${wait} min or choose a different type.` };
  }
  // SOS bypasses spam/profanity — apply rate limit only
  const rate = checkRateLimit(true);
  if (!rate.allowed) return { valid: false, reason: rate.reason };
  return { valid: true };
}

export function recordSOSSubtype(sosSubtype) {
  _recentSOS[sosSubtype ?? 'other'] = Date.now();
}

// ── Firestore helpers ─────────────────────────────────────────────
export async function confirmReport(reportId, db) {
  if (!db) return;
  const { doc, updateDoc, increment } = await import('firebase/firestore');
  await withRetry(
    () => updateDoc(doc(db, 'reports', reportId), { confirmCount: increment(1) }),
    { label: 'confirmReport' }
  );
}

export async function flagReport(reportId, db) {
  if (!db) return;
  const { doc, updateDoc, increment } = await import('firebase/firestore');
  await withRetry(
    () => updateDoc(doc(db, 'reports', reportId), { flagCount: increment(1) }),
    { label: 'flagReport' }
  );
}

// ── Internal helpers ──────────────────────────────────────────────
function _normalizeText(text) {
  return (text ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')  // strip diacritics
    .replace(/[^a-z0-9\s]/g, ' ')    // keep only alphanumeric + spaces
    .replace(/\s+/g, ' ')
    .trim();
}

function _bigrams(text) {
  const set = new Set();
  for (let i = 0; i < text.length - 1; i++) {
    set.add(text.slice(i, i + 2));
  }
  return set;
}

function _diceSimilarity(a, b) {
  if (!a || !b) return 0;
  if (a === b)  return 1;
  const ba = _bigrams(a);
  const bb = _bigrams(b);
  let intersection = 0;
  for (const bigram of ba) {
    if (bb.has(bigram)) intersection++;
  }
  return (2 * intersection) / (ba.size + bb.size);
}
